/* eslint-disable @typescript-eslint/no-shadow */

import {
  SdgAlignmentsBySdgAndLevel,
  SdgAlignmentStrength,
  SdgAlignmentValence,
  SdgId,
} from '@upright/common/model/output/scores/sdg-alignment/types';
import {
  getAlignmentsOfValenceWithAdjustedValues,
  getMostAlignedSdgs,
} from '@upright/common/model/output/scores/sdg-alignment/utils';
import { Bounds, CarouselLayout, Side } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { sideToValence, toBounds } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/utils';
import { toSdgAligmentValence } from '@upright/ui/common/upright/core/impact-lenses/sdg/common/utils';
import { SdgUnitsMode } from '@upright/common/model/output/scores/sdg-alignment/types';
import { scaleLinear } from 'd3-scale';

export interface SdgLensLayout {
  sdgBoxArea: (side: Side, sdg: SdgId) => Bounds;
  sdgBoxBarRangeArea: (side: Side, sdg: SdgId) => Bounds;
  sdgBoxStrongBarArea: (side: Side, sdg: SdgId) => Bounds;
  sdgBoxNormalBarArea: (side: Side, sdg: SdgId) => Bounds;
  sdgBoxWeakBarArea: (side: Side, sdg: SdgId) => Bounds;
  getSdgTextsAreaBounds: (
    side: Side,
    sdg: SdgId,
  ) => {
    weak?: Bounds;
    normal?: Bounds;
    strong?: Bounds;
    sum: Bounds;
    isBelowBox: boolean;
  };
  narrowMode: boolean;
}

export function sdgLensLayout(
  base: CarouselLayout,
  alignments: SdgAlignmentsBySdgAndLevel,
  unitsMode: SdgUnitsMode = SdgUnitsMode.PURE_REVENUE_SHARES,
): SdgLensLayout {
  const sdgBoxVerticalSpacing = 10;
  const outerPadding = getOuterPadding(unitsMode);
  const fullWidth = base.fullArea().width;

  const narrowMode = fullWidth < 800;
  const belowBoxTextsAreaHeight = narrowMode ? 50 : 0;

  function getLineSpaceWidth() {
    const da = base.dataArea(Side.RIGHT);
    const available = da.width - getSdgBoxHeight();
    if (narrowMode) {
      return available;
    } else {
      const proportion = 0.2;
      return Math.max(available * proportion, 30);
    }
  }

  function getSdgBoxHeight() {
    const fullWidth = base.fullArea().width;
    if (fullWidth > 1000) {
      return 100;
    }
    return scaleLinear().domain([320, 1000]).range([45, 100])(fullWidth) ?? 0;
  }

  const sdgBoxHeight = getSdgBoxHeight();
  const lineSpaceWidth = getLineSpaceWidth();

  function sdgBoxArea(side: Side, sdg: SdgId) {
    const da = base.dataArea(side);
    const sdgIds = getMostAlignedSdgs(alignments, toSdgAligmentValence(sideToValence(side)), false);
    const boxesHeight =
      sdgIds.length * (sdgBoxHeight + belowBoxTextsAreaHeight) + (sdgIds.length - 1) * sdgBoxVerticalSpacing;
    const startY = da.cy - boxesHeight / 2;

    const index = sdgIds.indexOf(sdg);
    const y1 = startY + index * (sdgBoxHeight + sdgBoxVerticalSpacing + belowBoxTextsAreaHeight);
    const y2 = y1 + sdgBoxHeight;

    function getXValues() {
      if (side === Side.RIGHT) {
        const x1 = da.x1 + lineSpaceWidth;
        const x2 = x1 + sdgBoxHeight;
        return { x1, x2 };
      } else {
        const x2 = da.x2 - lineSpaceWidth;
        const x1 = x2 - sdgBoxHeight;
        return { x1, x2 };
      }
    }

    const { x1, x2 } = getXValues();
    return toBounds({ x1, x2, y1, y2 });
  }

  function sdgBoxBarRangeArea(side: Side, id: SdgId) {
    const ba = sdgBoxArea(side, id);
    const da = base.dataArea(side);
    const margin = 4;

    function getXValues() {
      if (side === Side.RIGHT) {
        const x1 = ba.x2 + margin;
        const x2 = da.x2 - outerPadding;
        return { x1, x2 };
      } else {
        const x2 = ba.x1 - margin;
        const x1 = da.x1 + outerPadding;
        return { x1, x2 };
      }
    }

    const { x1, x2 } = getXValues();
    const y1 = ba.y1;
    const y2 = ba.y2;
    return toBounds({ x1, x2, y1, y2 });
  }

  function sdgBarScale(side: Side, id: SdgId) {
    const rangeArea = sdgBoxBarRangeArea(side, id);
    const scale = scaleLinear().domain([0, 1]).range([0, rangeArea.width]);
    return scale;
  }

  function pickValueForSdgAndSideAndStrength(side: Side, id: SdgId, strength: SdgAlignmentStrength) {
    const alignmentsOfSdg = alignments[id];
    const { strong, normal, weak } = getAlignmentsOfValenceWithAdjustedValues(
      alignmentsOfSdg,
      side === Side.RIGHT ? SdgAlignmentValence.ALIGNED : SdgAlignmentValence.MISALIGNED,
      unitsMode,
    );
    switch (strength) {
      case SdgAlignmentStrength.STRONG: {
        return strong.adjustedValue;
      }
      case SdgAlignmentStrength.NORMAL: {
        return normal.adjustedValue;
      }
      case SdgAlignmentStrength.WEAK: {
        return weak.adjustedValue;
      }
    }
  }

  function sdgBoxStrongBarArea(side: Side, id: SdgId) {
    const ra = sdgBoxBarRangeArea(side, id);
    const scale = sdgBarScale(side, id);
    const value = pickValueForSdgAndSideAndStrength(side, id, SdgAlignmentStrength.STRONG);

    function getXValues() {
      if (side === Side.RIGHT) {
        const x1 = ra.x1;
        const x2 = x1 + (scale(value) ?? 0);
        return { x1, x2 };
      } else {
        const x2 = ra.x2;
        const x1 = x2 - (scale(value) ?? 0);
        return { x1, x2 };
      }
    }

    const { x1, x2 } = getXValues();

    return toBounds({ x1, x2, y1: ra.y1, y2: ra.y2 });
  }

  function sdgBoxNormalBarArea(side: Side, id: SdgId) {
    const ra = sdgBoxStrongBarArea(side, id);
    const scale = sdgBarScale(side, id);
    const value = pickValueForSdgAndSideAndStrength(side, id, SdgAlignmentStrength.NORMAL);

    function getXValues() {
      if (side === Side.RIGHT) {
        const x1 = ra.x2;
        const x2 = x1 + (scale(value) ?? 0);
        return { x1, x2 };
      } else {
        const x2 = ra.x1;
        const x1 = x2 - (scale(value) ?? 0);
        return { x1, x2 };
      }
    }

    const { x1, x2 } = getXValues();
    return toBounds({ x1, x2, y1: ra.y1, y2: ra.y2 });
  }

  function sdgBoxWeakBarArea(side: Side, id: SdgId) {
    const ra = sdgBoxNormalBarArea(side, id);
    const scale = sdgBarScale(side, id);
    const value = pickValueForSdgAndSideAndStrength(side, id, SdgAlignmentStrength.WEAK);

    function getXValues() {
      if (side === Side.RIGHT) {
        const x1 = ra.x2;
        const x2 = x1 + (scale(value) ?? 0);
        return { x1, x2 };
      } else {
        const x2 = ra.x1;
        const x1 = x2 - (scale(value) ?? 0);
        return { x1, x2 };
      }
    }

    const { x1, x2 } = getXValues();
    return toBounds({ x1, x2, y1: ra.y1, y2: ra.y2 });
  }

  function getSdgTextsAreaBounds(side: Side, id: SdgId) {
    // In narrow mode, no score breakdown is shown so we just return the bounds for the text
    // box that shows the sum.
    if (narrowMode) {
      const boxArea = sdgBoxArea(side, id);
      const y1 = boxArea.y2;
      const y2 = y1 + belowBoxTextsAreaHeight;
      if (side === Side.RIGHT) {
        const x1 = boxArea.x1;
        const x2 = x1 + outerPadding;
        return { isBelowBox: true, sum: { ...toBounds({ x1, x2, y1, y2 }) } };
      } else {
        const x2 = boxArea.x2;
        const x1 = x2 - outerPadding;
        return { isBelowBox: true, sum: { ...toBounds({ x1, x2, y1, y2 }) } };
      }
    }

    // This area is used to show the sum outside the bar in summary percentage mode
    function getTextAreaOutsideBar(): Bounds {
      // The weak bar area is the outernmost, so we use that as a base to determine the text location
      const baseArea = sdgBoxWeakBarArea(side, id);
      const { y1, y2 } = baseArea;
      const textMargin = 15;

      if (side === Side.RIGHT) {
        const x1 = baseArea.x2 + textMargin;
        const x2 = x1 + outerPadding;
        return toBounds({ y1, y2, x1, x2 });
      } else {
        const x2 = baseArea.x1 - textMargin;
        const x1 = x2 - outerPadding;
        return toBounds({ y1, y2, x1, x2 });
      }
    }

    return {
      isBelowBox: false,
      sum: getTextAreaOutsideBar(),

      // The texts for pure revenue share sdg values are inside the bars, so we can use
      // the bar areas directly
      weak: sdgBoxWeakBarArea(side, id),
      normal: sdgBoxNormalBarArea(side, id),
      strong: sdgBoxStrongBarArea(side, id),
    };
  }

  return {
    sdgBoxArea,
    sdgBoxBarRangeArea,
    sdgBoxStrongBarArea,
    sdgBoxWeakBarArea,
    sdgBoxNormalBarArea,
    getSdgTextsAreaBounds,
    narrowMode,
  };
}

function getOuterPadding(unitsMode: SdgUnitsMode) {
  switch (unitsMode) {
    case SdgUnitsMode.SUMMARY_PERCENTAGE:
      // In the summary percentage mode, we'll leave some space to the sides to show the text value.
      return 105;
    case SdgUnitsMode.PURE_REVENUE_SHARES:
      return 0;
  }
}
